import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProjectPanel = ({ projects, selectedProject, onProjectSelect, onNewProject, timeEntries }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success bg-success/10';
      case 'on-hold': return 'text-warning bg-warning/10';
      case 'completed': return 'text-muted-foreground bg-muted/30';
      default: return 'text-primary bg-primary/10';
    }
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 90) return 'bg-error';
    if (percentage >= 75) return 'bg-warning';
    return 'bg-success';
  };

  const handleProjectClick = (project, event) => {
    // Prevent event bubbling and ensure clean selection
    event?.preventDefault();
    event?.stopPropagation();
    
    if (onProjectSelect && typeof onProjectSelect === 'function') {
      onProjectSelect(project);
    }
  };

  const handleNewProjectClick = (event) => {
    // Prevent event bubbling and ensure clean action
    event?.preventDefault();
    event?.stopPropagation();
    
    if (onNewProject && typeof onNewProject === 'function') {
      onNewProject();
    }
  };

  return (
    <div className="w-80 bg-surface border-r border-border h-full flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">Active Projects</h2>
          <Button
            variant="default"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            onClick={handleNewProjectClick}
            className="hover:bg-primary/90 active:bg-primary/80 focus:ring-2 focus:ring-primary/50"
          >
            New Project
          </Button>
        </div>
        <div className="text-sm text-muted-foreground">
          {projects?.filter(p => p?.status === 'active')?.length || 0} active projects
        </div>
      </div>
      
      {/* Projects List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {projects?.length > 0 ? (
          projects?.map((project) => (
            <div
              key={project?.id}
              onClick={(event) => handleProjectClick(project, event)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e?.key === 'Enter' || e?.key === ' ') {
                  e?.preventDefault();
                  handleProjectClick(project, e);
                }
              }}
              className={`p-4 rounded-lg border cursor-pointer transition-all duration-150 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                selectedProject?.id === project?.id
                  ? 'border-primary bg-primary/5 shadow-sm ring-2 ring-primary/20'
                  : 'border-border hover:border-primary/50 hover:bg-muted/30 active:bg-muted/50'
              }`}
            >
              {/* Project Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-foreground text-sm mb-1 truncate" title={project?.name}>
                    {project?.name}
                  </h3>
                  <p className="text-xs text-muted-foreground truncate" title={project?.client}>
                    {project?.client}
                  </p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ml-2 ${getStatusColor(project?.status)}`}>
                  {project?.status}
                </span>
              </div>

              {/* Progress Bar */}
              <div className="mb-3">
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                  <span>{project?.hoursSpent}h / {project?.budgetedHours}h</span>
                  <span>{Math.round((project?.hoursSpent / project?.budgetedHours) * 100)}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${getProgressColor((project?.hoursSpent / project?.budgetedHours) * 100)}`}
                    style={{ width: `${Math.min((project?.hoursSpent / project?.budgetedHours) * 100, 100)}%` }}
                  />
                </div>
              </div>

              {/* Project Stats */}
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-1 text-muted-foreground">
                    <Icon name="Calendar" size={12} />
                    <span>{project?.deadline}</span>
                  </div>
                  <div className="flex items-center space-x-1 text-muted-foreground">
                    <Icon name="DollarSign" size={12} />
                    <span>${project?.hourlyRate}/hr</span>
                  </div>
                </div>
                {project?.hoursSpent > project?.budgetedHours && (
                  <div className="flex items-center space-x-1 text-error">
                    <Icon name="AlertTriangle" size={12} />
                    <span>Over budget</span>
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="mb-4">
              <img 
                src="/assets/images/image-1761885647241.png" 
                alt="Time tracking illustration showing productivity and project management"
                className="w-32 h-32 object-contain opacity-60"
              />
            </div>
            <p className="text-sm text-muted-foreground mb-4">No projects found</p>
            <Button
              variant="outline"
              size="sm"
              iconName="Plus"
              iconPosition="left"
              onClick={handleNewProjectClick}
            >
              Create Your First Project
            </Button>
          </div>
        )}
      </div>
      
      {/* Summary */}
      <div className="p-4 border-t border-border bg-muted/20">
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Total Hours Today:</span>
            <span className="font-medium text-foreground">
              {timeEntries?.filter(entry => entry?.date === new Date()?.toISOString()?.split('T')?.[0])
                ?.reduce((sum, entry) => sum + (entry?.duration || 0), 0)?.toFixed(1)}h
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Billable Hours:</span>
            <span className="font-medium text-success">
              {timeEntries?.filter(entry => entry?.date === new Date()?.toISOString()?.split('T')?.[0] && entry?.billable)
                ?.reduce((sum, entry) => sum + (entry?.duration || 0), 0)?.toFixed(1)}h
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Revenue Today:</span>
            <span className="font-medium text-foreground">
              ${timeEntries?.filter(entry => entry?.date === new Date()?.toISOString()?.split('T')?.[0] && entry?.billable)
                ?.reduce((sum, entry) => {
                  const project = projects?.find(p => p?.id === entry?.projectId);
                  return sum + ((entry?.duration || 0) * (project?.hourlyRate || 0));
                }, 0)?.toFixed(2)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectPanel;